import type { AiSkillsJournalEntry, AiSkillsOverview } from '@/api/aiSkillsAdmin';
import type { AppConfigDocument } from '@/api/appConfig';

/**
 * Тумблеры ИИ-заданий глазами администратора (docs/CONTRACTS/ai-skills.md §3–4).
 *
 * Ученику — ручка `GET /ielts/ai-skills` (`lib/aiSkills.ts`); здесь — два уровня
 * по отдельности: сервер (документ настроек) и курс (`ai_skills_off`).
 */

/**
 * Порядок тумблеров на экране. Тот же список — CHECK миграции courses 000022.
 * Мока и спринта нет намеренно (владелец 13.10.2026): они собираются из
 * включённых навыков, а мок назначает куратор окном в «Расписании тренажёров».
 */
export const AI_SKILL_KEYS = ['reading', 'listening', 'writing', 'speaking'] as const;
export type AiSkillKey = (typeof AI_SKILL_KEYS)[number];

/** Ключ настройки сервера: `reading` → `features.ai_skills.reading`. */
export function aiSkillConfigKey(skill: AiSkillKey): string {
  return `features.ai_skills.${skill}`;
}

/**
 * Включён ли навык на сервере.
 *
 * ⚠️ ОТСУТСТВИЕ КЛЮЧА — «ВЫКЛЮЧЕН», в отличие от `isFeatureEnabled`, где оно
 * значит «включена». Навыки закрыты, пока их явно не открыли (прежний кран
 * D26), и показать админу «включено» там, где ученик ничего не видит, — соврать.
 * В документе ключ плоский: users режет `features.ai_skills.reading` по первой
 * точке, и в `features` он лежит как `ai_skills.reading`.
 */
export function aiSkillServerOn(doc: AppConfigDocument | undefined, skill: AiSkillKey): boolean {
  return doc?.features?.[`ai_skills.${skill}`] === true;
}

// ── Сводка (заход 2, 16.10.2026) ──────────────────────────────────────
//
// ⚠️ ТЕКСТЫ — НЕ ЗДЕСЬ, А В pages/admin/aiSkillsServerText.ts. Сторож слов ученика
// (`studentFacingWords.test.ts`) считает строку ученической, если её ключ упомянут в
// файле на ученическом пути, а `lib/` — такой путь: ключ сотрудника здесь сделал бы
// весь раздел «видимым ученику».

/**
 * Кто видит карточку тумблеров сервера: роли PUT /admin/app-config и сводки —
 * admin и director. Manager страницу флагов открывает, но сервер ему отказывает;
 * карточка, которая отвечает 403 на каждый щелчок, хуже отсутствующей.
 */
export function canSeeServerCard(role: string | undefined): boolean {
  return role === 'admin' || role === 'director';
}

/** Числа навыка из сводки; нет сводки — null (числа не обещаем). */
export function skillCounts(o: AiSkillsOverview | undefined, skill: AiSkillKey): { now: number; after: number } | null {
  const s = o?.skills?.find((x) => x.skill === skill);
  return s ? { now: s.now, after: s.after_mock } : null;
}

/**
 * Listening без готовых записей (решение владельца 16.10.2026): включать нельзя —
 * ученик получил бы пустой тренажёр. Только предупреждение, щелчок не запрещён:
 * решает владелец. Нет сводки — не предупреждаем (не знаем).
 */
export function listeningEmpty(o: AiSkillsOverview | undefined, skill: AiSkillKey): boolean {
  return skill === 'listening' && !!o && o.listening.ready === 0;
}

/** Журнал из ответа: пустой срез Go может прийти null — экран на .length не падает. */
export function journalOf(o: AiSkillsOverview | undefined): AiSkillsJournalEntry[] {
  return Array.isArray(o?.journal) ? o.journal : [];
}

/**
 * Новый список выключенного у курса после щелчка по тумблеру. Порядок —
 * канонический, чтобы сохранение не зависело от порядка щелчков.
 */
export function nextCourseOff(off: readonly string[], skill: AiSkillKey, nextOn: boolean): string[] {
  const set = new Set(off);
  if (nextOn) set.delete(skill);
  else set.add(skill);
  return AI_SKILL_KEYS.filter((k) => set.has(k));
}
